import { useCallback, useEffect, useRef, useState } from "react";
import {
	Database,
	Shield,
	CreditCard,
	Menu,
	PackageOpen,
	Download,
	Upload,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
	getImportExportCounts,
	exportData,
	importData,
} from "@/services/api";

type DataType =
	| "features"
	| "role-permissions"
	| "plans"
	| "menu-items"
	| "all";

interface CardConfig {
	type: DataType;
	title: string;
	icon: React.ElementType;
	bgColor: string;
	iconColor: string;
	borderColor: string;
	countKey: "features" | "roles" | "plans" | "menuItems" | null;
}

const cards: CardConfig[] = [
	{
		type: "features",
		title: "Features",
		icon: Database,
		bgColor: "bg-blue-50",
		iconColor: "text-blue-600",
		borderColor: "border-blue-200",
		countKey: "features",
	},
	{
		type: "role-permissions",
		title: "Role Permissions",
		icon: Shield,
		bgColor: "bg-purple-50",
		iconColor: "text-purple-600",
		borderColor: "border-purple-200",
		countKey: "roles",
	},
	{
		type: "plans",
		title: "Plans",
		icon: CreditCard,
		bgColor: "bg-green-50",
		iconColor: "text-green-600",
		borderColor: "border-green-200",
		countKey: "plans",
	},
	{
		type: "menu-items",
		title: "Menu Items",
		icon: Menu,
		bgColor: "bg-orange-50",
		iconColor: "text-orange-600",
		borderColor: "border-orange-200",
		countKey: "menuItems",
	},
];

export function ImportExport() {
	const { toast } = useToast();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [counts, setCounts] = useState<{
		features: number;
		roles: number;
		plans: number;
		menuItems: number;
	} | null>(null);
	const [loading, setLoading] = useState(true);
	const [exporting, setExporting] = useState<DataType | null>(null);
	const [importing, setImporting] = useState<DataType | null>(null);
	const [pendingImport, setPendingImport] = useState<{
		type: DataType;
		data: unknown;
		fileName: string;
	} | null>(null);
	const importTypeRef = useRef<DataType | null>(null);

	const fetchCounts = useCallback(async () => {
		try {
			setLoading(true);
			const data = await getImportExportCounts();
			setCounts(data);
		} catch {
			toast({
				title: "Error",
				description: "Failed to load counts.",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	}, [toast]);

	useEffect(() => {
		fetchCounts();
	}, [fetchCounts]);

	const handleExport = async (type: DataType) => {
		try {
			setExporting(type);
			const data = await exportData(type);
			const blob = new Blob([JSON.stringify(data, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `flow-metrics-${type}-${new Date().toISOString().slice(0, 10)}.json`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
			toast({
				title: "Export Successful",
				description: `${type === "all" ? "All data" : type} exported successfully.`,
			});
		} catch {
			toast({
				title: "Export Failed",
				description: `Failed to export ${type}.`,
				variant: "destructive",
			});
		} finally {
			setExporting(null);
		}
	};

	const triggerImport = (type: DataType) => {
		importTypeRef.current = type;
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !importTypeRef.current) return;

		const reader = new FileReader();
		reader.onload = (event) => {
			try {
				const data = JSON.parse(event.target?.result as string);
				setPendingImport({
					type: importTypeRef.current!,
					data,
					fileName: file.name,
				});
			} catch {
				toast({
					title: "Invalid File",
					description: "The selected file is not valid JSON.",
					variant: "destructive",
				});
			}
		};
		reader.readAsText(file);
		e.target.value = "";
	};

	const confirmImport = async () => {
		if (!pendingImport) return;
		const { type, data } = pendingImport;
		try {
			setImporting(type);
			setPendingImport(null);
			await importData(type, data);
			toast({
				title: "Import Successful",
				description: `${type === "all" ? "All data" : type} imported successfully.`,
			});
			fetchCounts();
		} catch {
			toast({
				title: "Import Failed",
				description: `Failed to import ${type}.`,
				variant: "destructive",
			});
		} finally {
			setImporting(null);
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold text-gray-900">
					Import / Export
				</h1>
				<p className="text-gray-500 mt-1">
					Export or import configuration data for backup and migration.
				</p>
			</div>

			{/* Cards Grid */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{cards.map((card) => {
					const Icon = card.icon;
					const count =
						counts && card.countKey ? counts[card.countKey] : null;
					return (
						<div
							key={card.type}
							className={`rounded-lg border ${card.borderColor} ${card.bgColor} p-5`}
						>
							<div className="flex items-center gap-3 mb-4">
								<div
									className={`p-2 rounded-lg ${card.bgColor} ${card.iconColor}`}
								>
									<Icon className="h-5 w-5" />
								</div>
								<div>
									<h3 className="font-semibold text-gray-900">
										{card.title}
									</h3>
									<p className="text-sm text-gray-500">
										{loading
											? "Loading..."
											: count !== null
												? `${count} records`
												: ""}
									</p>
								</div>
							</div>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={() => handleExport(card.type)}
									disabled={exporting === card.type}
									className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
								>
									<Download className="h-3.5 w-3.5" />
									{exporting === card.type
										? "Exporting..."
										: "Export"}
								</button>
								<button
									type="button"
									onClick={() => triggerImport(card.type)}
									disabled={importing === card.type}
									className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
								>
									<Upload className="h-3.5 w-3.5" />
									{importing === card.type
										? "Importing..."
										: "Import"}
								</button>
							</div>
						</div>
					);
				})}
			</div>

			{/* All Data Section */}
			<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
				<div className="flex items-center gap-3 mb-4">
					<div className="p-2 rounded-lg bg-gray-100 text-gray-600">
						<PackageOpen className="h-5 w-5" />
					</div>
					<div>
						<h3 className="font-semibold text-gray-900">
							All Data
						</h3>
						<p className="text-sm text-gray-500">
							Export or import all configuration data at once.
						</p>
					</div>
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => handleExport("all")}
						disabled={exporting === "all"}
						className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
					>
						<Download className="h-3.5 w-3.5" />
						{exporting === "all" ? "Exporting..." : "Export All"}
					</button>
					<button
						type="button"
						onClick={() => triggerImport("all")}
						disabled={importing === "all"}
						className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
					>
						<Upload className="h-3.5 w-3.5" />
						{importing === "all" ? "Importing..." : "Import All"}
					</button>
				</div>
			</div>

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept=".json"
				className="hidden"
				onChange={handleFileChange}
			/>

			{/* Confirmation Modal */}
			{pendingImport && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Confirm Import
						</h3>
						<p className="text-sm text-gray-600 mb-1">
							You are about to import{" "}
							<span className="font-medium">
								{pendingImport.type === "all"
									? "all data"
									: pendingImport.type}
							</span>{" "}
							from:
						</p>
						<p className="text-sm font-mono bg-gray-100 rounded px-2 py-1 mb-3">
							{pendingImport.fileName}
						</p>
						<div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
							<p className="text-sm text-yellow-800">
								<strong>Warning:</strong> This will overwrite
								existing data. This action cannot be undone.
								Make sure you have a backup before proceeding.
							</p>
						</div>
						<div className="flex justify-end gap-2">
							<button
								type="button"
								onClick={() => setPendingImport(null)}
								className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmImport}
								className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
							>
								Confirm Import
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
